import type { ReactNode } from 'react';

import { Card, MonoLabel, cn } from '@/design-system';
import { formatMoney, isZeroAmount, type MonthSummary } from '@/entities/invoice';

import { billingCopy } from '../copy';

export interface MonthCardProps {
    summary: MonthSummary;
}

/**
 * De donkere maandkaart (scherm 10, rechtsonder): gefactureerd, vergoeding
 * inspecteurs, en achter een 1px scheiding de marge. Daaronder, alleen als er
 * iets openstaat, wat nog betaald moet worden en wat daarvan te laat is.
 *
 * Omzet en marge, geen herstelkosten: de kleuren van de kostenblokken horen hier
 * niet (patterns.md §3).
 */
export function MonthCard({ summary }: MonthCardProps) {
    return (
        <Card variant="dark" padding="md" as="section" aria-label={summary.periodLabel}>
            <div className="mb-[10px] flex items-center justify-between gap-3">
                <MonoLabel size={10} tone="onDarkMuted" className="tracking-[0.1em]">
                    {summary.periodLabel}
                </MonoLabel>
                <MonoLabel size={10} variant="value" tone="onDarkMuted">
                    {billingCopy.monthInvoiceCount(summary.invoiceCount)}
                </MonoLabel>
            </div>

            <Row label={billingCopy.invoiced} value={formatMoney(summary.invoiced)} />
            <Row label={billingCopy.inspectorFees} value={formatMoney(summary.inspectorFees)} />

            <div className="flex items-center justify-between gap-3 border-t border-[var(--ink-500)] pt-[10px] text-[15px] leading-[1.3]">
                <span className="text-[var(--text-on-dark)]">{billingCopy.margin}</span>
                <span className="font-[family-name:var(--font-display)] font-semibold text-[var(--text-on-dark)]">
                    {formatMoney(summary.margin)}
                </span>
            </div>

            {isZeroAmount(summary.outstanding) ? null : (
                <div className="mt-3 border-t border-[var(--ink-500)] pt-[10px]">
                    <Row
                        label={billingCopy.outstanding}
                        value={formatMoney(summary.outstanding)}
                        last={summary.overdueCount === 0}
                    />
                    {summary.overdueCount > 0 ? (
                        <Row
                            label={billingCopy.overdue(summary.overdueCount)}
                            value={formatMoney(summary.overdue)}
                            last
                        />
                    ) : null}
                </div>
            )}
        </Card>
    );
}

function Row({ label, value, last = false }: { label: ReactNode; value: string; last?: boolean }) {
    return (
        <div
            className={cn(
                'flex items-center justify-between gap-3 text-[13px] leading-[1.4]',
                !last && 'mb-2',
            )}
        >
            <span className="text-[var(--text-on-dark-2)]">{label}</span>
            <span className="font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-on-dark-2)]">
                {value}
            </span>
        </div>
    );
}
